import { useMemo, useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { buildSuggestedQueries } from '@realadvisor/constants/source-match';
import { Building2, Users } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { OrganisationSelect } from '@modules/_shared/components/organisation-select/OrganisationSelect';
import { SegmentedToggleButtonGroup } from '@modules/_shared/components/SegmentedToggleButtonGroup';
import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';
import { useTenantIntegrations } from '@modules/_shared/hooks/useTenantIntegrations';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  FieldLabel,
  OrganisationAccountFields,
} from '@modules/organisations/components/OrganisationAccountFields';
import { CREATE_ORGANISATION } from '@modules/organisations/organisationsQueries';
import { type SourceMatch } from '@modules/teams/components/MatchSuggestionField';
import { CREATE_TEAM } from '@modules/teams/teamQueries';
import { useLocale } from '@shared/hooks/locale';
import { isRATenantId } from '@shared/utils/tenant';

const outlinedFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'background.paper',
  },
};

export const TeamCreateDrawer = () => {
  const navigate = useNavigateWithSearchParams();
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const { me } = useAppData();
  const showAccountFields =
    me?.is_admin === true && isRATenantId(me.tenant_id ?? '');
  const { hasStripeKey, hasCloseKey } = useTenantIntegrations();
  const [step, setStep] = useState<1 | 2>(1);
  const [teamName, setTeamName] = useState('');
  const [organisationMode, setOrganisationMode] = useState<'existing' | 'new'>(
    'new',
  );
  const [organisationId, setOrganisationId] = useState<string | null>(null);
  const [organisationName, setOrganisationName] = useState('');
  const [organisationNameTouched, setOrganisationNameTouched] = useState(false);
  const [selectedStripe, setSelectedStripe] = useState<SourceMatch[]>([]);
  const [selectedClose, setSelectedClose] = useState<SourceMatch | null>(null);
  const [linkedTenantId, setLinkedTenantId] = useState<string | null>(null);
  const [salesRepUserId, setSalesRepUserId] = useState<string | null>(null);
  const [csmRepUserId, setCsmRepUserId] = useState<string | null>(null);
  const [createTeam, { loading: createTeamLoading }] = useMutation(CREATE_TEAM);
  const [createOrganisation, { loading: createOrganisationLoading }] =
    useMutation(CREATE_ORGANISATION);

  const submitting = createTeamLoading || createOrganisationLoading;
  const trimmedTeamName = teamName.trim();
  const canContinue = trimmedTeamName.length > 0;
  const canSave =
    !submitting && (organisationMode === 'new' || organisationId != null);

  const suggestedQueries = useMemo(
    () =>
      buildSuggestedQueries(
        [trimmedTeamName, organisationName.trim()].filter(Boolean),
      ),
    [trimmedTeamName, organisationName],
  );

  const handleClose = () => navigate('../');

  const handleTeamNameChange = (value: string) => {
    setTeamName(value);
    if (!organisationNameTouched) {
      setOrganisationName(value);
    }
  };

  const addStripeMatch = (match: SourceMatch) => {
    setSelectedStripe(matches =>
      matches.some(item => item.id === match.id)
        ? matches
        : [...matches, match],
    );
  };

  const handleSubmit = async () => {
    const name = trimmedTeamName;
    if (!name) {
      return;
    }

    let selectedOrganisationId = organisationId;
    if (organisationMode === 'new') {
      const newOrganisationName = organisationName.trim() || name;

      const organisationResult = await createOrganisation({
        variables: {
          organisation: {
            name: newOrganisationName,
            ...(showAccountFields && {
              stripe_customer_ids: selectedStripe.map(match => match.id),
              close_lead_id: selectedClose?.id ?? null,
              linked_tenant_id: linkedTenantId,
              sales_rep_user_id: salesRepUserId,
              csm_rep_user_id: csmRepUserId,
            }),
          },
        },
      });
      selectedOrganisationId =
        organisationResult.data?.insert_organisations_one?.id ?? null;
    }

    if (!selectedOrganisationId) {
      return;
    }

    return createTeam({
      variables: {
        team: {
          name,
          organisation_id: selectedOrganisationId,
        },
      },
    }).then(res => {
      navigate({
        pathname: `../${res.data?.insert_teams_one?.id}/overview`,
        search: searchParams.toString(),
      });
    });
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (step === 1) {
      if (canContinue) {
        setStep(2);
      }
      return;
    }
    if (canSave) {
      void handleSubmit();
    }
  };

  return (
    <Drawer
      onClose={handleClose}
      title={t('Create team')}
      slotProps={{
        paper: {
          sx: {
            width: ['100%', '100%', 800],
          },
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleFormSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }}
      >
        <Stack spacing={2} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {step === 1 ? (
            <>
              <Typography variant="caption" color="text.secondary">
                {t('Step 1 of 2')}
              </Typography>
              <Box>
                <FieldLabel>{t('Team name')}</FieldLabel>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder={t('Team name')}
                  value={teamName}
                  onChange={event => handleTeamNameChange(event.target.value)}
                  autoFocus
                  sx={outlinedFieldSx}
                />
              </Box>
            </>
          ) : (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('Step 2 of 2')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'Link the team to the right organisation and CRM records.',
                  )}
                </Typography>
              </Box>

              <Stack spacing={1.5}>
                <Typography variant="subtitle2">{t('Organisation')}</Typography>
                <SegmentedToggleButtonGroup
                  fullWidth
                  value={organisationMode}
                  onChange={setOrganisationMode}
                  options={[
                    {
                      value: 'new',
                      label: (
                        <>
                          <Building2 size={16} />
                          {t('New organisation')}
                        </>
                      ),
                    },
                    {
                      value: 'existing',
                      label: (
                        <>
                          <Users size={16} />
                          {t('Existing organisation')}
                        </>
                      ),
                    },
                  ]}
                  sx={{
                    gap: 1,
                    '& .MuiToggleButtonGroup-grouped': {
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      textTransform: 'none',
                      gap: 1,
                      bgcolor: 'background.paper',
                    },
                  }}
                />

                {organisationMode === 'existing' ? (
                  <Box>
                    <FieldLabel>{t('Organisation')}</FieldLabel>
                    <OrganisationSelect
                      onChange={setOrganisationId}
                      initialSearchQuery={trimmedTeamName}
                      InputProps={{ placeholder: t('Search organisation') }}
                    />
                  </Box>
                ) : (
                  <Box>
                    <FieldLabel>{t('Organisation name')}</FieldLabel>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder={t('Organisation name')}
                      value={organisationName}
                      onChange={event => {
                        setOrganisationNameTouched(true);
                        setOrganisationName(event.target.value);
                      }}
                      sx={outlinedFieldSx}
                    />
                  </Box>
                )}
              </Stack>

              {showAccountFields && organisationMode === 'new' && (
                <>
                  <Divider />
                  <OrganisationAccountFields
                    suggestedQueries={suggestedQueries}
                    hasStripeKey={hasStripeKey}
                    hasCloseKey={hasCloseKey}
                    stripeMatches={selectedStripe}
                    onAddStripe={addStripeMatch}
                    onRemoveStripe={id =>
                      setSelectedStripe(matches =>
                        matches.filter(item => item.id !== id),
                      )
                    }
                    closeMatch={selectedClose}
                    onSelectClose={setSelectedClose}
                    salesRepUserId={salesRepUserId}
                    setSalesRepUserId={setSalesRepUserId}
                    csmRepUserId={csmRepUserId}
                    setCsmRepUserId={setCsmRepUserId}
                    linkedTenantId={linkedTenantId}
                    setLinkedTenantId={setLinkedTenantId}
                    tenantMatchName={organisationName.trim() || trimmedTeamName}
                  />
                </>
              )}
            </>
          )}
        </Stack>

        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            gap: 1,
            p: 2,
            borderTop: '1px solid',
            borderColor: 'grey.200',
            bgcolor: 'background.paper',
          }}
        >
          {step === 1 ? (
            <>
              <Button type="button" onClick={handleClose}>
                {t('Cancel')}
              </Button>
              <Button type="submit" variant="contained" disabled={!canContinue}>
                {t('Continue')}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" onClick={() => setStep(1)}>
                {t('Back')}
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                loading={submitting}
                disabled={!canSave}
              >
                {t('Save')}
              </LoadingButton>
            </>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
};
